import { ChangeDetectionStrategy, Component } from '@angular/core'

import { formatPesos, formatPesosCompact } from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { formatMetricValue, formatMonth, RENT_SOURCE_LABELS } from '../risk-scorecard.labels'
import { FlowsBlock, FlowsMonth } from '@data/interfaces/risk-scorecard.interface'
import { RiskScorecardTabBase } from '../risk-scorecard-tab.base'

/** Alto de la gráfica mensual (la misma de Cash flow en Análisis). */
const CHART_HEIGHT = 150
/** Alto mínimo de una barra: un mes en 0 se sigue viendo como raya. */
const MIN_BAR = 2
//* Con menos de 12 meses con dato no hay un año de flujo para medir la renta (K3 queda parcial).
const FULL_WINDOW_MONTHS = 12

/** Un mes de la tabla, con el acumulado del neto desde el primer mes de la ventana. */
export interface FlowRow extends FlowsMonth {
  cumulative: number
}

/**
 * Flujos del cliente (plan v4.2 §8b.3): el flujo mensual por CFDI de la foto, sus promedios y cuánto cubre la renta.
 * Es la pestaña «Flujos del cliente» del Excel. El del obligado solidario no se replica: no tiene RFC ni CIEC.
 */
@Component({
  selector: 'app-ciec-flows-tab',
  templateUrl: './ciec-flows-tab.component.html',
  styleUrls: ['./ciec-flows-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CiecFlowsTabComponent extends RiskScorecardTabBase {
  flows: FlowsBlock | null = null
  rows: FlowRow[] = []
  bars: Array<{ inflow: number; outflow: number; month: string }> = []

  readonly pesos = formatPesos
  readonly compact = formatPesosCompact
  readonly month = formatMonth
  readonly rentSources = RENT_SOURCE_LABELS
  readonly averages = [
    { key: 'm3', label: 'Promedio 3 meses' },
    { key: 'm6', label: 'Promedio 6 meses' },
    { key: 'm12', label: 'Promedio 12 meses' },
  ] as const

  /** Menos de 12 meses con dato. */
  get shortWindow(): boolean {
    return this.rows.length > 0 && this.rows.length < FULL_WINDOW_MONTHS
  }

  get hasRent(): boolean {
    return (this.flows?.rentCoverage?.monthlyRent ?? 0) > 0
  }

  coverage(value: number | null | undefined): string {
    return formatMetricValue(value, 'x')
  }

  protected override onResult(): void {
    this.flows = this.result?.flows ?? null
    let cumulative = 0
    //* La tabla va del mes más reciente al más viejo; el acumulado corre desde el primero de la ventana.
    this.rows = (this.flows?.monthly ?? [])
      .map((month) => {
        cumulative += month.net
        return { ...month, cumulative }
      })
      .reverse()
    const months = this.flows?.monthly ?? []
    const max = Math.max(0, ...months.map((month) => Math.max(month.inflow, Math.abs(month.outflow))))
    this.bars = months.map((month) => ({ month: month.month, inflow: bar(month.inflow, max), outflow: bar(Math.abs(month.outflow), max) }))
  }
}

function bar(value: number, max: number): number {
  if (!(max > 0) || !(value > 0)) return MIN_BAR
  return Math.max(MIN_BAR, Math.round((value / max) * CHART_HEIGHT))
}
